import React from "react";
import { BasicModal } from "../../BasicModal/BasicModal";
import { ReactComponent as BTCIcon } from "../../../SVGcomponents/Bitcoin.svg";
import BasicInput from "../../../Inputs/BasicInput";
import BasicButton, {
  RESPONSIVE_BREAKPOINTS,
} from "../../../Buttons/BasicButton";
import styles from "./styles.module.scss";
import axios from "../../../../api/axios";
import { triggerGlobalNotifications } from "../../../../context/ErrorBridge";
import { useNotification } from "../../../../context/NotificationContext";
import { LedgerPaymentStep } from "./LedgerPaymentStep";
import { CryptoDeposit } from "./types";
import ledgerStyles from "./ledgerPayment.module.scss";

type Props = {
  closeModal: () => void;
  isOpened: boolean;
};

const LeftIcon = () => {
  return <span className={styles["left-icon"]}>$</span>;
};

// Minimum top-up in USD, the same for card, crypto and invoice: a single
// top-up of $100 also takes a Trial account to Starter on the backend.
const MINIMUM_PAYMENT_AMOUNT = 100;

export const AddFundByCryptoModal = ({ closeModal, isOpened }: Props) => {
  const [inputValue, setInputValue] = React.useState<string>("");
  const [isError, setIsError] = React.useState<boolean>(false);
  const [showMinimumError, setShowMinimumError] =
    React.useState<boolean>(false);
  const { callNotification } = useNotification();
  // Set once the backend answers with a Ledger payment (address + amount);
  // without it the form asks for the amount.
  const [deposit, setDeposit] = React.useState<CryptoDeposit | null>(null);
  // What the admin's Off / Test / Live switch allows this account: whether a
  // crypto top-up can be started, and whether it would be a test one.
  const [access, setAccess] = React.useState<{
    available: boolean;
    testnet: boolean;
  }>({ available: true, testnet: false });

  // An unpaid payment whose window is still open comes back on reopening, so
  // closing the modal by mistake loses nothing. The backend also reads the
  // chain here: a late payment it has just credited opens as "received".
  React.useEffect(() => {
    if (!isOpened) {
      setDeposit(null);
      return;
    }
    let cancelled = false;
    axios
      .get("/payments/crypto/active")
      .then(({ data }) => {
        if (cancelled) return;
        if (data?.crypto) setDeposit(data.crypto);
        setAccess({
          available: data?.available !== false,
          testnet: Boolean(data?.testnet),
        });
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [isOpened]);

  const handleChange = (e: any) => {
    const newValue = e.currentTarget.value;
    setInputValue(newValue);

    const isValidNumber = /^\d*\.?\d*$/.test(newValue);

    if (!isValidNumber || newValue === ".") {
      setIsError(true);
      setShowMinimumError(false);
      return;
    }

    const numericValue = parseFloat(newValue);
    if (isNaN(numericValue) || numericValue < MINIMUM_PAYMENT_AMOUNT) {
      setIsError(true);
      setShowMinimumError(true);
      return;
    }

    setIsError(false);
    setShowMinimumError(false);
  };

  const handleSubmit = async () => {
    try {
      const { data } = await axios.post("/payments/createPayment/crypto", {
        amount: inputValue,
      });

      // Crypto is paid right here, straight to the Ledger; there is no
      // provider page to send the client to any more.
      if (!data?.crypto) throw new Error("No crypto payment in the response");
      setInputValue("");
      setDeposit(data.crypto);
    } catch (error: any) {
      // 400 (amount out of range) and 503 (crypto off, or every unique
      // amount taken) carry a message written for the client; anything else
      // stays generic.
      const message = error?.response?.data?.message;
      const readable =
        typeof message === "string" &&
        [400, 503].includes(error?.response?.status);
      triggerGlobalNotifications({
        type: "error",
        data: {
          message: readable
            ? message
            : "Failed to create crypto payment. Please try again.",
          placement: "top",
        },
      });
    }
    callNotification();
  };

  if (deposit) {
    return (
      <BasicModal
        sx={{ width: "482px" }}
        isOpened={isOpened}
        closeModal={closeModal}
      >
        <div className={styles["title-box"]}>
          <BTCIcon style={{ width: "21px", height: "21px" }} />
          <h3 className={styles["title"]}>Add funds</h3>
          <span className={styles["method-type"]}>
            Crypto · USDT{deposit.testnet ? " · Test" : ""}
          </span>
        </div>
        <LedgerPaymentStep
          key={deposit.hashId}
          deposit={deposit}
          onClose={closeModal}
          onNewPayment={() => setDeposit(null)}
        />
      </BasicModal>
    );
  }

  // The admin's switch is Off (or Test, and this account is not a tester):
  // nothing to fill in.
  if (!access.available) {
    return (
      <BasicModal
        sx={{ width: "482px" }}
        isOpened={isOpened}
        closeModal={closeModal}
      >
        <div className={styles["title-box"]}>
          <BTCIcon style={{ width: "21px", height: "21px" }} />
          <h3 className={styles["title"]}>Add funds</h3>
          <span className={styles["method-type"]}>Crypto</span>
        </div>
        <p className={ledgerStyles.unavailable}>
          Crypto top-ups are unavailable right now. Please choose another
          payment method.
        </p>
        <div className={styles["button-box"]}>
          <BasicButton
            type="button"
            size="large"
            onClick={closeModal}
            title="Close"
            variant="secondary"
            cls={styles.cancelButton}
          />
        </div>
      </BasicModal>
    );
  }

  return (
    <BasicModal
      sx={{ width: "482px" }}
      isOpened={isOpened}
      closeModal={closeModal}
    >
      <div className={styles["title-box"]}>
        <BTCIcon style={{ width: "21px", height: "21px" }} />
        <h3 className={styles["title"]}>Add funds</h3>
        <span className={styles["method-type"]}>
          Crypto{access.testnet ? " · Test" : ""}
        </span>
      </div>
      {access.testnet && (
        <p className={ledgerStyles.testNotice}>
          Test mode: you'll pay with test USDT on the TRON Nile testnet. Real
          USDT is not accepted yet.
        </p>
      )}
      <div className={styles["form"]}>
        <div className={styles["input-box"]}>
          <label>Amount</label>
          <BasicInput
            type="text"
            value={inputValue}
            onChange={handleChange}
            placeholder={"$ 0.00"}
            error={isError}
            cls={styles["modalInputWithIcon"]}
          />
          {showMinimumError && (
            <div className={styles.minimumError}>
              Minimum payment amount - ${MINIMUM_PAYMENT_AMOUNT}
            </div>
          )}
        </div>
      </div>
      <span className={styles["text"]}>
        By clicking the "Confirm" button above, you are agreeing
        <br />
        to our{" "}
        <a
          className={styles["textPrivacy"]}
          href="/terms-of-use"
          target="_blank"
          rel="noopener noreferrer"
        >
          Terms of Service
        </a>{" "}
        and acknowledge
        <br />
        that you have read our{" "}
        <a
          className={styles["textPrivacy"]}
          href="/privacy-policy"
          target="_blank"
          rel="noopener noreferrer"
        >
          Privacy Policy
        </a>
      </span>
      <div className={styles["button-box"]}>
        <BasicButton
          type="button"
          size="large"
          responsiveBreakpoints={{
            mac13: {
              mediaQuery: RESPONSIVE_BREAKPOINTS.MAC_13,
              customSize: {
                height: 40,
              },
            },
          }}
          onClick={closeModal}
          title="Cancel"
          variant="secondary"
          cls={styles.cancelButton}
        />
        <BasicButton
          type="button"
          size="large"
          responsiveBreakpoints={{
            mac13: {
              mediaQuery: RESPONSIVE_BREAKPOINTS.MAC_13,
              customSize: {
                height: 40,
              },
            },
          }}
          disabled={isError || !inputValue || !access.available}
          onClick={handleSubmit}
          title="Confirm"
          variant="primary"
          cls={styles.confirmButton}
        />
      </div>
    </BasicModal>
  );
};
